// generationTracker — a module-level store of in-flight AI generations.
//
// Why it exists: the atelier workbench keys each step tab's body
// component by tab (so per-tab state doesn't bleed). The side effect is
// that switching tabs UNMOUNTS the previous tab's component — taking its
// "still cooking" indicator and busy state with it. A generation started
// on the Proposal tab vanished from the UI the moment you looked at
// Voice Signet, even though the fetch was still running.
//
// This store lives ABOVE React's component tree. A generation registers
// here when it starts and deregisters when it settles; a single
// app-level <CookingChip/> (mounted in Shell) reads it and stays visible
// across tab switches, scrolls, and route changes. A remounted tab body
// can also query it to restore its own busy state.

export interface ActiveGen {
  /** Stable key — one in-flight generation per (session, tab). */
  id: string;
  sessionId: string;
  tab: string;            // internal tab key: title | cover | plan | ...
  label: string;          // human label for the chip, e.g. "Proposal"
  startedAt: number;      // epoch ms
  etaSeconds: number;
  /** Variant count on the tab when this generation fired — lets a
   *  remounted tab body's recovery poll know when something NEW landed. */
  startVariantCount: number;
}

const active = new Map<string, ActiveGen>();
const listeners = new Set<() => void>();

// Safety net: a generation whose fetch hangs and whose owning component
// unmounted (so no recovery poll) would otherwise pin the chip forever.
// Anything older than this is treated as stalled and dropped on read.
const MAX_AGE_MS = 10 * 60 * 1000;

function emit() {
  for (const fn of listeners) fn();
}

export function genKey(sessionId: string, tab: string): string {
  return `${sessionId}:${tab}`;
}

export function startGeneration(g: ActiveGen): void {
  active.set(g.id, g);
  emit();
}

export function endGeneration(id: string): void {
  if (active.delete(id)) emit();
}

/** Active generations, freshest first, with stalled ones pruned. */
export function getActiveGenerations(): ActiveGen[] {
  const now = Date.now();
  let pruned = false;
  for (const [id, g] of active) {
    if (now - g.startedAt > MAX_AGE_MS) { active.delete(id); pruned = true; }
  }
  if (pruned) emit();
  return [...active.values()].sort((a, b) => b.startedAt - a.startedAt);
}

/** The in-flight generation for a given tab, if any. A remounted tab
 *  body calls this to restore its busy state. */
export function getGenerationFor(sessionId: string, tab: string): ActiveGen | null {
  const g = active.get(genKey(sessionId, tab));
  if (!g) return null;
  if (Date.now() - g.startedAt > MAX_AGE_MS) { endGeneration(g.id); return null; }
  return g;
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
